// AuthContext.js
import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { onSnapshot, doc } from "firebase/firestore";
import { auth, db } from "../utils/firebase";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user,       setUser]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  // 'pending' | 'approved' | 'rejected' | null (null = admin or logged-out)
  const [userStatus, setUserStatus] = useState(null);

  useEffect(() => {
    let statusUnsub = null;

    const authUnsub = onAuthStateChanged(auth, (firebaseUser) => {
      // Tear down previous status listener whenever auth state changes
      if (statusUnsub) { statusUnsub(); statusUnsub = null; }

      setUser(firebaseUser);

      if (firebaseUser) {
        // Subscribe to the user's Firestore doc for real-time approval status.
        // For admin accounts that have no users/{uid} doc, snap.exists() is false
        // and userStatus stays null — ProtectedRoute handles the admin bypass.
        statusUnsub = onSnapshot(
          doc(db, "users", firebaseUser.uid),
          (snap) => {
            setUserStatus(snap.exists() ? (snap.data().status ?? null) : null);
            setLoading(false);
          },
          () => {
            // Permission denied or network error — fail open for loading, gate
            // enforcement happens in ProtectedRoute.
            setUserStatus(null);
            setLoading(false);
          }
        );
      } else {
        setUserStatus(null);
        setLoading(false);
      }
    });

    return () => {
      authUnsub();
      if (statusUnsub) statusUnsub();
    };
  }, []);

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, logout, loading, userStatus }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
